import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import LessonCarouselCard from './LessonCarouselCard';
import { useCoach } from '../../contexts/CoachContext';

/**
 * LessonCarousel - Display upcoming lessons in a horizontal scrollable carousel
 * Supports both group lessons and individual (1-on-1) sessions
 * @param {Object} props
 * @param {Array} [props.lessons] - Lessons array to display (group + individual, sorted)
 * @param {boolean} [props.loading] - Show loading state
 * @param {Object} [props.currentUser] - Current authenticated user
 * @param {number} [props.userCredits] - User's credit balance for this coach
 * @param {Array} [props.enrolledLessonIds] - IDs of group lessons user is enrolled in
 * @param {Function} [props.onRegister] - Callback when user registers for a group lesson
 * @param {Function} [props.onBookSlot] - Callback when user books an individual session slot
 */
const LessonCarousel = ({
  lessons = [],
  loading = false,
  currentUser = null,
  userCredits = 0,
  enrolledLessonIds = [],
  onRegister = null,
  onBookSlot = null
}) => {
  const scrollRef = useRef(null);
  const coachContext = useCoach();
  const [needsScroll, setNeedsScroll] = useState(false);

  // Check if scrolling is needed
  useEffect(() => {
    const checkScrollNeeded = () => {
      if (scrollRef.current) {
        const { scrollWidth, clientWidth } = scrollRef.current;
        setNeedsScroll(scrollWidth > clientWidth);
      }
    };

    checkScrollNeeded();
    window.addEventListener('resize', checkScrollNeeded);
    return () => window.removeEventListener('resize', checkScrollNeeded);
  }, [lessons]);

  // Use provided lessons (no fallback to mock data)
  const displayLessons = lessons;


  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="lesson-carousel-section">
      <div className="container">
        <div className="carousel-header">
          <div className="header-text">
            <h2>Upcoming Lessons</h2>
            <p>Browse the schedule and register for your next class</p>
          </div>
        </div>
      </div>

      <div className="carousel-wrapper">
        {loading ? (
          <div className="carousel-loading">
            <div className="carousel-spinner"></div>
            <span>Loading lessons...</span>
          </div>
        ) : displayLessons.length === 0 ? (
          <div className="carousel-empty">
            <p>No upcoming lessons scheduled.</p>
          </div>
        ) : (
          <div className="carousel-scroll" ref={scrollRef}>
            <div className="carousel-spacer"></div>
            {displayLessons.map((lesson) => (
              <LessonCarouselCard
                key={lesson.id}
                lesson={lesson}
                currentUser={currentUser}
                userCredits={userCredits}
                isEnrolled={enrolledLessonIds.includes(lesson.id)}
                onRegister={onRegister}
                onBookSlot={onBookSlot}
              />
            ))}
            <div className="carousel-spacer"></div>
          </div>
        )}
      </div>

      {needsScroll && displayLessons.length > 0 && !loading && (
        <div className="container">
          <div className="carousel-nav">
            <button
              className="nav-btn"
              onClick={() => scroll('left')}
              aria-label="Scroll left"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              className="nav-btn"
              onClick={() => scroll('right')}
              aria-label="Scroll right"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      )}

      <style>{`
        .lesson-carousel-section {
          padding: 0 0 32px;
          background: transparent;
        }

        .carousel-header {
          margin-bottom: 20px;
        }

        .header-text {
          text-align: left;
        }

        .header-text h2 {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--gc-text);
          margin-bottom: 8px;
        }

        .header-text p {
          font-size: 1.1rem;
          color: var(--gc-text-muted);
        }

        .carousel-nav {
          display: flex;
          justify-content: flex-start;
          gap: 12px;
          padding-top: 24px;
        }

        .nav-btn {
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--gc-bg-card);
          border: 1px solid var(--gc-border);
          border-radius: 10px;
          color: var(--gc-text);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .nav-btn:hover {
          background: var(--gc-gradient);
          border-color: transparent;
          color: white;
        }

        .nav-btn svg {
          width: 24px;
          height: 24px;
          stroke: currentColor;
          flex-shrink: 0;
          display: block;
        }

        .carousel-wrapper {
          position: relative;
          width: 100%;
          overflow: hidden;
        }

        .carousel-scroll {
          display: flex;
          gap: 24px;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          padding: 20px 0 24px;
          scroll-padding-left: 24px;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .carousel-scroll::-webkit-scrollbar {
          display: none;
        }

        .carousel-spacer {
          min-width: calc((100vw - 1200px) / 2);
          flex-shrink: 0;
        }

        @media (max-width: 1280px) {
          .carousel-spacer {
            min-width: 24px;
          }
        }

        /* Loading and Empty States */
        .carousel-loading,
        .carousel-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 24px;
          color: var(--gc-text-muted);
        }

        .carousel-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(116, 149, 85, 0.2);
          border-top-color: #749555;
          border-radius: 50%;
          animation: carousel-spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes carousel-spin {
          to { transform: rotate(360deg); }
        }

        .carousel-empty p {
          font-size: 1rem;
          color: var(--gc-text-light);
        }

        @media (max-width: 768px) {
          .lesson-carousel-section {
            padding: 0 0 24px;
          }

          .header-text h2 {
            font-size: 1.5rem;
          }

          .carousel-spacer {
            min-width: 16px;
          }

          .carousel-scroll {
            gap: 16px;
          }
        }

        @media (max-width: 480px) {
          .lesson-carousel-section {
            padding: 0 0 20px;
          }

          .header-text h2 {
            font-size: 1.25rem;
          }

          .header-text p {
            font-size: 0.95rem;
          }

          .carousel-spacer {
            min-width: 12px;
          }

          .carousel-scroll {
            gap: 12px;
          }

          .carousel-header {
            margin-bottom: 20px;
          }
        }
      `}</style>
    </section>
  );
};

export default LessonCarousel;
